import { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';
import identity from 'lodash/identity';
import { BOOKING_LIST_LOAD_LIMIT } from 'config/constants';
import { BookingListContextValue } from './types';
import { getBookingCountLabel, getCurrentFilterLabel } from './utils';
import { useFilter } from '../hooks';
import { DEFAULT_FILTER, OPTIONS, VALID_FILTERS } from './constants';
import sendGAEvent from 'utils/sendGAEvent';
import { GA, SelfServiceAPI as SelfServiceAPIType } from 'types';
import sumBy from 'lodash/sumBy';
import capitalize from 'lodash/capitalize';
import { SelfServiceAPI } from 'apis';
import uniqBy from 'lodash/uniqBy';
import * as track from 'lib/gaTracking/bookingListPageEvents';

const BookingListContext = createContext<BookingListContextValue>({
  options: OPTIONS,
  filter: DEFAULT_FILTER,
  update: identity,
  loading: false,
  bookingListLabel: getCurrentFilterLabel(DEFAULT_FILTER),
  bookingCountLabel: getBookingCountLabel(),
  bookings: [],
  hasMore: false,
  loadMore: identity,
  error: null,
  sendViewBookingEvent: () => {},
});

const BookingListProvider: FC = ({ children }) => {
  const { filter, update: updateFilter } = useFilter({ validFilters: VALID_FILTERS, defaultFilter: DEFAULT_FILTER });
  const [bookings, setBookings] = useState<
    SelfServiceAPIType.UpcomingBooking[] | SelfServiceAPIType.PastBooking[] | SelfServiceAPIType.CancelledBooking[]
  >([]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  const update = useCallback(
    (newFilter: string) => {
      updateFilter(newFilter);
      setBookings([]);
      setHasMore(false);
      setError(null);
      setTotalBookings(0);
    },
    [updateFilter],
  );

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await SelfServiceAPI.getBookings(
          {
            limit: BOOKING_LIST_LOAD_LIMIT,
            offset: 0,
            filter: filter,
          },
          { signal: controller.signal },
        );

        setBookings(result.bookings);
        setHasMore(result.hasMore);
        setTotalBookings(result.totalCount);
      } catch (err: any) {
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [filter]);

  const loadMore = useCallback(async () => {
    if (!bookings.length) return;

    setLoading(true);

    const offset = bookings.length;
    track.loadMoreButton(offset, filter);

    try {
      const result = await SelfServiceAPI.getBookings({
        limit: BOOKING_LIST_LOAD_LIMIT,
        offset,
        filter,
      });

      setBookings((prev) => uniqBy([...prev, ...result.bookings], 'id'));
      setHasMore(result.hasMore);
      setTotalBookings(result.totalCount);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [bookings, filter]);

  const sendViewBookingEvent = (id: string) => {
    const index = bookings.findIndex((booking) => booking.id === id);

    if (index < 0) {
      return;
    }

    const {
      reservation: { property, roomType },
      nights,
      payableTotals,
    } = bookings[index];

    sendGAEvent({
      type: GA.EventType.SELECT_ITEM,
      itemListId: `${filter}_bookings`,
      itemListName: `${capitalize(filter)} Bookings`,
      currency: payableTotals[0].currency,
      items: [
        {
          itemId: id,
          itemName: property.name,
          quantity: nights,
          itemVariant: roomType.name,
          itemCategory: 'Hotel',
          index,
          price: sumBy(payableTotals, 'amount'),
        },
      ],
    });
  };

  const contextValue = {
    options: OPTIONS,
    filter,
    update,
    loading,
    bookingListLabel: getCurrentFilterLabel(filter),
    bookingCountLabel: getBookingCountLabel(totalBookings),
    bookings,
    hasMore,
    loadMore,
    error,
    sendViewBookingEvent,
  };

  return <BookingListContext.Provider value={contextValue}>{children}</BookingListContext.Provider>;
};

export default BookingListProvider;
export const useBookingList = () => useContext(BookingListContext);
